import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import SparkCounter from "../components/SparkCounter";
import ResmoMascot from "../components/ResmoMascot";
import ResultSection from "../components/ResultSection";
import SectionExpandToolbar from "../components/SectionExpandToolbar";
import OutcomeWidget from "../components/OutcomeWidget";
import ProgressMeter from "../components/ProgressMeter";
import IntegrityPanel from "../components/IntegrityPanel";
import StudioPublishPanel from "../components/studio/StudioPublishPanel";
import OverviewTab from "../components/dashboard-result/OverviewTab";
import { ReportTabStrip } from "../components/reports/ScoreAnalysis";
import TailoredResumeTab from "../components/dashboard-result/TailoredResumeTab";
import CoverLetterTab from "../components/dashboard-result/CoverLetterTab";
import ResearchInterviewTab from "../components/dashboard-result/ResearchInterviewTab";
import ShareModal from "../components/dashboard-result/ShareModal";
import RetailorModal from "../components/dashboard-result/RetailorModal";
import AddInterviewModal from "../components/AddInterviewModal";
import JobMatchesPanel from "../components/JobMatchesPanel";
import {
  ArrowLeft, Globe, MessageSquare, GitCompare,
  Loader, Mail, Share2, Layers, Sparkles, Calendar, Send, TrendingUp,
} from "lucide-react";
import jsPDF from "jspdf";
import { tailoredToResumeText } from "../lib/tailoredToResumeText";

export default function Dashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [showRetailor, setShowRetailor] = useState(false);
  const [showAddInterview, setShowAddInterview] = useState(false);
  const [tonePreset, setTonePreset] = useState("");
  const [customTone, setCustomTone] = useState("");
  const [retailoring, setRetailoring] = useState(false);
  const [retailorError, setRetailorError] = useState("");
  const [openingStudio, setOpeningStudio] = useState(false);
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchError, setResearchError] = useState("");
  const [interviewLoading, setInterviewLoading] = useState(false);
  const [interviewError, setInterviewError] = useState("");
  const [coverLoading, setCoverLoading] = useState(false);
  const [coverError, setCoverError] = useState("");
  const [coverCopied, setCoverCopied] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareCopied, setShareCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [parent, setParent] = useState(null);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    if (!analysis?.job_url) return;
    api.get(`/outcomes/by-analysis/${id}`)
      .then((r) => { if (r.data?.id) setApplied(true); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, analysis?.job_url]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/analyses/${id}`);
        setAnalysis(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!analysis?.based_on) { setParent(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/analyses/${analysis.based_on}`);
        setParent({
          id: data.id,
          job_title: data.job_title,
          company_name: data.company_name,
          fit_score: ((data.result || {}).fit_assessment || {}).overall_score,
        });
      } catch { setParent(null); }
    })();
  }, [analysis?.based_on]);

  const pollJob = async (jobId, onComplete, onError) => {
    const pollUntil = Date.now() + 1000 * 60 * 3;
    while (Date.now() < pollUntil) {
      await new Promise((r) => setTimeout(r, 2500));
      try {
        const { data: status } = await api.get(`/jobs/${jobId}`);
        if (status.status === "complete") { await onComplete(status); return; }
        if (status.status === "error") { onError(status.error || "Failed"); return; }
      } catch { /* keep polling */ }
    }
    onError("Resmo took too long. Try again.");
  };

  const refreshAnalysis = async () => {
    try {
      const { data } = await api.get(`/analyses/${id}`);
      setAnalysis(data);
    } catch { /* ignore */ }
  };

  const handleRequestResearch = async () => {
    setResearchError(""); setResearchLoading(true);
    try {
      const { data: jobData } = await api.post(`/analyses/${id}/research`);
      await pollJob(
        jobData.job_id,
        async () => { await refreshAnalysis(); setResearchLoading(false); },
        (msg) => { setResearchLoading(false); setResearchError(msg); },
      );
    } catch (e) {
      setResearchLoading(false);
      setResearchError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleRequestInterview = async () => {
    setInterviewError(""); setInterviewLoading(true);
    try {
      const { data: jobData } = await api.post(`/analyses/${id}/interview-prep`);
      await pollJob(
        jobData.job_id,
        async () => { await refreshAnalysis(); setInterviewLoading(false); },
        (msg) => { setInterviewLoading(false); setInterviewError(msg); },
      );
    } catch (e) {
      setInterviewLoading(false);
      setInterviewError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleRequestCover = async (tone = null) => {
    setCoverError(""); setCoverLoading(true);
    try {
      const { data: jobData } = await api.post(`/analyses/${id}/cover-letter`, tone ? { tone } : {});
      await pollJob(
        jobData.job_id,
        async () => { await refreshAnalysis(); setCoverLoading(false); },
        (msg) => { setCoverLoading(false); setCoverError(msg); },
      );
    } catch (e) {
      setCoverLoading(false);
      setCoverError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const buildCoverLetterText = () => {
    const c = analysis?.cover_letter;
    if (!c) return "";
    return [c.greeting || "", "", c.hook || "", "", c.body || "", "", c.close || "", "", c.sign_off || "", c.signature || ""].join("\n").trim();
  };

  const handleCopyCover = async () => {
    const text = buildCoverLetterText();
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCoverCopied(true);
    setTimeout(() => setCoverCopied(false), 2000);
  };

  const handleDownloadCoverPdf = () => {
    const c = analysis?.cover_letter;
    if (!c) return;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 64;
    const maxWidth = 612 - margin * 2;
    let y = margin + 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    if (c.subject_line) {
      doc.setFont("helvetica", "bold");
      doc.text(`Subject: ${c.subject_line}`, margin, y);
      y += 24;
      doc.setFont("helvetica", "normal");
    }
    const blocks = [c.greeting, c.hook, c.body, c.close, c.sign_off, c.signature].filter(Boolean);
    blocks.forEach((block) => {
      const lines = doc.splitTextToSize(block, maxWidth);
      lines.forEach((line) => {
        if (y > 760) { doc.addPage(); y = margin; }
        doc.text(line, margin, y);
        y += 16;
      });
      y += 10;
    });
    const filename = `cover-letter-${(analysis?.company_name || "company").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
    doc.save(filename);
  };

  const handleEmailDraftCover = () => {
    const c = analysis?.cover_letter;
    if (!c) return;
    const subject = encodeURIComponent(c.subject_line || `Application for ${analysis?.job_title || "open role"}`);
    const body = encodeURIComponent(buildCoverLetterText());
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCreateShare = async () => {
    setShareLoading(true);
    try {
      const { data } = await api.post(`/analyses/${id}/share`);
      setShareUrl(data.share_url);
      setShowShare(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setShareLoading(false);
    }
  };

  const handleRevokeShare = async () => {
    try {
      await api.delete(`/analyses/${id}/share`);
      setShareUrl("");
      setShowShare(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleCopyShare = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  if (error) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center" data-testid="dashboard-error">
          <ResmoMascot state="hungry" size={180} />
          <div className="font-display text-3xl mt-4 uppercase">Couldn't load that meal</div>
          <p className="font-mono-rm text-sm mt-2">{error}</p>
          <button onClick={() => navigate("/feed-job")} className="rm-btn rm-btn-primary mt-6">Try again</button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center" data-testid="dashboard-loading">
          <ResmoMascot state="digesting" size={180} flavor="resume" />
          <div className="font-display text-3xl mt-4 uppercase">Loading the goods...</div>
        </div>
      </div>
    );
  }

  const r = analysis.result || {};
  const problem = r.problem_assessment || {};
  const company = r.company_insight || {};
  const fit = r.fit_assessment || {};
  const tweaks = r.tweaks || [];
  const tailored = r.tailored_resume || {};

  const fitDelta =
    typeof fit.overall_score === "number" && typeof parent?.fit_score === "number"
      ? fit.overall_score - parent.fit_score
      : null;

  const buildResumeText = () => {
    const lines = [];
    lines.push((tailored.headline || "").toUpperCase());
    lines.push("");
    if (tailored.summary) { lines.push("SUMMARY"); lines.push(tailored.summary); lines.push(""); }
    if ((tailored.key_skills || []).length) {
      lines.push("KEY SKILLS");
      lines.push((tailored.key_skills || []).join(" · "));
      lines.push("");
    }
    if ((tailored.experience || []).length) {
      lines.push("EXPERIENCE");
      tailored.experience.forEach((exp) => {
        lines.push(`${exp.title || ""} — ${exp.company || ""} (${exp.dates || ""})`);
        (exp.bullets || []).forEach((b) => lines.push(`  • ${b}`));
        lines.push("");
      });
    }
    if ((tailored.education || []).length) {
      lines.push("EDUCATION");
      tailored.education.forEach((ed) => {
        lines.push(`${ed.degree || ""} — ${ed.school || ""} (${ed.dates || ""})`);
      });
      lines.push("");
    }
    if ((tailored.additional || []).length) {
      lines.push("ADDITIONAL");
      tailored.additional.forEach((a) => lines.push(`• ${a}`));
    }
    return lines.join("\n");
  };

  const handleCopy = async () => {
    const text = buildResumeText();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 50;
    const maxWidth = pageWidth - margin * 2;
    let y = margin;
    const writeLine = (text, opts = {}) => {
      const fontSize = opts.size || 11;
      const bold = opts.bold || false;
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(text || "", maxWidth);
      lines.forEach((ln) => {
        if (y > 760) { doc.addPage(); y = margin; }
        doc.text(ln, margin, y);
        y += fontSize * 1.3;
      });
    };
    writeLine((tailored.headline || analysis.job_title || "Tailored Resume").toUpperCase(), { size: 18, bold: true });
    y += 6;
    if (tailored.summary) {
      writeLine("SUMMARY", { size: 11, bold: true });
      writeLine(tailored.summary);
      y += 6;
    }
    if ((tailored.key_skills || []).length) {
      writeLine("KEY SKILLS", { size: 11, bold: true });
      writeLine((tailored.key_skills || []).join(" · "));
      y += 6;
    }
    if ((tailored.experience || []).length) {
      writeLine("EXPERIENCE", { size: 11, bold: true });
      tailored.experience.forEach((exp) => {
        writeLine(`${exp.title || ""} — ${exp.company || ""}  (${exp.dates || ""})`, { bold: true });
        (exp.bullets || []).forEach((b) => writeLine(`• ${b}`));
        y += 4;
      });
    }
    if ((tailored.education || []).length) {
      writeLine("EDUCATION", { size: 11, bold: true });
      tailored.education.forEach((ed) => writeLine(`${ed.degree || ""} — ${ed.school || ""} (${ed.dates || ""})`));
      y += 6;
    }
    if ((tailored.additional || []).length) {
      writeLine("ADDITIONAL", { size: 11, bold: true });
      tailored.additional.forEach((a) => writeLine(`• ${a}`));
    }
    const filename = `${(analysis.job_title || "tailored-resume").replace(/[^a-z0-9]+/gi, "-")}.pdf`;
    doc.save(filename);
  };

  const handleDownloadDocx = async () => {
    try {
      const res = await api.get(`/analyses/${id}/docx`, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = (analysis.job_title || "tailored-resume").replace(/[^a-z0-9]+/gi, "-");
      link.download = `${safeName}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleRetailor = async () => {
    setRetailorError("");
    const tone = (customTone || tonePreset || "").trim();
    setRetailoring(true);
    try {
      const { data: jobData } = await api.post(`/analyses/${id}/retailor`, { tone_hint: tone || null });
      const jobId = jobData.job_id;
      const pollUntil = Date.now() + 1000 * 60 * 3;
      while (Date.now() < pollUntil) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const { data: status } = await api.get(`/jobs/${jobId}`);
          if (status.status === "complete" && status.analysis_id) {
            setRetailoring(false);
            setShowRetailor(false);
            navigate(`/dashboard/${status.analysis_id}`);
            return;
          }
          if (status.status === "error") {
            setRetailoring(false);
            setRetailorError(status.error || "Re-tailor failed.");
            return;
          }
        } catch { /* keep polling */ }
      }
      setRetailoring(false);
      setRetailorError("Resmo took too long. Try again.");
    } catch (e) {
      setRetailoring(false);
      setRetailorError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Push the tailored resume into the user's Studio draft so they can tweak
  // it further with live ATS scoring + the auto-patcher. The studio draft is
  // a single doc per user (upserted), so this OVERWRITES any prior draft —
  // we warn before proceeding.
  const handleOpenInStudio = async () => {
    const text = tailoredToResumeText(tailored);
    if (!text || text.trim().length < 40) {
      setError("This tailored resume looks empty — re-tailor it first, then open in Studio.");
      return;
    }
    const proceed = window.confirm(
      "Open this tailored resume in Resmo Studio?\n\n" +
      "This will replace your current Studio draft with the tailored version " +
      "so you can tweak it with live ATS scoring and auto-patch suggestions.",
    );
    if (!proceed) return;
    setOpeningStudio(true);
    try {
      await api.post("/studio/save", {
        content_md: text,
        template_id: "classic_chronological",
        based_on_analysis_id: id,
        applied_ids: [],
      });
      navigate(`/studio?analysis_id=${encodeURIComponent(id)}`);
    } catch (e) {
      setOpeningStudio(false);
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleApply = async () => {
    window.open(analysis.job_url, "_blank", "noopener");
    if (applied) return;
    try {
      await api.post("/outcomes", {
        analysis_id: id,
        status: "applied",
        notes: "Applied via Resmo → employer site",
      });
      setApplied(true);
      window.dispatchEvent(new CustomEvent("resmo:spark", { detail: { kind: "apply" } }));
      window.dispatchEvent(new CustomEvent("resmo:outcome:refresh", { detail: { analysisId: id } }));
    } catch { /* non-fatal — the posting still opened */ }
  };

  return (
    <div className="min-h-screen rm-paper pb-20">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <button onClick={() => navigate("/history")} className="rm-tag mb-3" data-testid="back-to-history-btn">
              <ArrowLeft size={14} /> History
            </button>
            {analysis.based_on && (
              parent ? (
                <Link
                  to={`/dashboard/${parent.id}`}
                  className="rm-tag mb-3 ml-2 hover:opacity-80 transition-opacity"
                  style={{ background: "#FEF3C7", borderColor: "#0F1B2D" }}
                  data-testid="retailored-from-badge"
                  title="Open original analysis"
                >
                  <Layers size={12} /> Re-tailored from {parent.job_title || "earlier version"}
                </Link>
              ) : (
                <span
                  className="rm-tag mb-3 ml-2"
                  style={{ background: "#FEF3C7", borderColor: "#0F1B2D" }}
                  data-testid="retailored-from-badge"
                >
                  <Layers size={12} /> Re-tailored
                </span>
              )
            )}
            <h1 className="font-display text-3xl md:text-5xl uppercase leading-none">
              {analysis.job_title || "Tailored Output"}
            </h1>
            {analysis.company_name && (
              <div className="font-mono-rm text-sm mt-2 uppercase tracking-widest" style={{ color: "#475569" }}>
                @ {analysis.company_name}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <SparkCounter />
            <ResmoMascot state="satisfied" size={140} flavor="resume" />
          </div>
        </div>

        {/* Score improvement celebration — re-check beat the original */}
        {fitDelta !== null && fitDelta > 0 && (
          <div className="flex items-center gap-4 flex-wrap px-5 py-4 mb-6"
               data-testid="fit-delta-banner"
               style={{ background: "#0E5A4A", color: "white", border: "3px solid #0F1B2D",
                        boxShadow: "4px 4px 0 0 #FFC145", borderRadius: 8 }}>
            <TrendingUp size={30} style={{ color: "#FFC145" }} />
            <div>
              <div className="font-display text-xl md:text-2xl leading-none" data-testid="fit-delta-scores">
                Your fit went {parent.fit_score} → {fit.overall_score}
                <span className="ml-2 px-2 py-0.5 text-sm align-middle rounded"
                      style={{ background: "#FFC145", color: "#0F1B2D", fontWeight: 800 }}
                      data-testid="fit-delta-amount">
                  +{fitDelta}
                </span>
              </div>
              <div className="text-xs mt-1.5" style={{ color: "#A7F3D0" }}>
                Your Studio edits paid off — this version is a stronger match. Time to apply.
              </div>
            </div>
          </div>
        )}

        {/* Action bar */}
        <div className="flex flex-wrap gap-3 mb-6" data-testid="dashboard-actions">
          {analysis.job_url && (
            <button
              onClick={handleApply}
              className="rm-btn text-xs px-4 py-2"
              style={{ background: applied ? "#0E5A4A" : "#B45309", color: "white", border: "2px solid #0F1B2D", boxShadow: "3px 3px 0 0 #0F1B2D", fontWeight: 700 }}
              data-testid="apply-on-site-btn"
              title="Opens the employer's application page in a new tab and logs 'Applied' on this analysis"
            >
              <Send size={14} /> {applied ? "Applied ✓ · Reopen posting" : "Apply on employer site"}
            </button>
          )}
          <button onClick={handleRequestResearch} disabled={researchLoading} className="rm-btn rm-btn-secondary text-xs px-4 py-2" data-testid="research-btn">
            {researchLoading ? <Loader size={14} className="animate-spin" /> : <Globe size={14} />}
            {researchLoading ? "Digging..." : analysis.company_research ? "Refresh research" : "Research the company"}
          </button>
          <button onClick={handleRequestInterview} disabled={interviewLoading} className="rm-btn rm-btn-dark text-xs px-4 py-2" data-testid="interview-prep-btn">
            {interviewLoading ? <Loader size={14} className="animate-spin" /> : <MessageSquare size={14} />}
            {interviewLoading ? "Coaching..." : analysis.interview_prep ? "Refresh interview prep" : "Interview prep"}
          </button>
          <button onClick={() => handleRequestCover()} disabled={coverLoading} className="rm-btn rm-btn-secondary text-xs px-4 py-2" data-testid="cover-letter-btn">
            {coverLoading ? <Loader size={14} className="animate-spin" /> : <Mail size={14} />}
            {coverLoading ? "Writing..." : analysis.cover_letter ? "Refresh cover letter" : "Cover letter"}
          </button>
          <button onClick={handleCreateShare} disabled={shareLoading} className="rm-btn rm-btn-ghost text-xs px-4 py-2" data-testid="share-btn">
            <Share2 size={14} /> Share
          </button>
          {analysis.based_on && (
            <Link to={`/compare/${analysis.based_on}/${analysis.id}`} className="rm-btn rm-btn-ghost text-xs px-4 py-2" data-testid="compare-with-parent-btn">
              <GitCompare size={14} /> Compare with original
            </Link>
          )}
        </div>

        {(researchLoading || interviewLoading || coverLoading) && (
          <div className="mb-6 max-w-md" data-testid="dashboard-task-progress">
            <ProgressMeter
              expectedSeconds={researchLoading ? 30 : interviewLoading ? 35 : 25}
              label={researchLoading ? "Researching the company" : interviewLoading ? "Building interview prep" : "Writing your cover letter"}
              testid="dashboard-task-progress-meter"
            />
          </div>
        )}

        {(researchError || interviewError || coverError) && (
          <div className="mb-6 p-3 rounded-md font-mono-rm text-sm" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}>
            {researchError || interviewError || coverError}
          </div>
        )}

        <SectionExpandToolbar
          group="dashboard-result"
          targetSelector='[data-testid="dashboard-sections"]'
          pdfTitle={`Job report · ${analysis.job_title || analysis.company_name || "Tailored application"}`}
          pdfFilename={`resmo-dashboard-${(analysis.job_title || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`}
        />

        {/* Strategy Brief CTA + Outcome tracker — top-of-result quick actions */}
        <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded"
             style={{ background: "#FFF2EC", border: "2px dashed #FF6A00" }}
             data-testid="dashboard-quick-actions">
          <button
            type="button"
            onClick={() => navigate(`/strategy-brief?analysis_id=${id}`)}
            className="rm-btn rm-btn-primary text-xs px-3 py-2"
            data-testid="dashboard-build-brief-btn"
            title="Generate a one-page strategic briefing (10 credits, Pro=free)"
          >
            <Sparkles size={13} /> Build a Role Strategy Brief
          </button>
          <button
            type="button"
            onClick={() => setShowAddInterview(true)}
            className="rm-btn text-xs px-3 py-2"
            style={{ background: "#047857", color: "white", border: "2px solid #064E3B", boxShadow: "3px 3px 0 0 #064E3B", fontWeight: 700 }}
            data-testid="dashboard-add-interview-btn"
            title="Schedule the interview for this role · Triggers 7d/24h/1h reminders"
          >
            <Calendar size={13} /> Schedule the interview
          </button>
          <div className="flex-1 min-w-0">
            <OutcomeWidget analysisId={id} compact />
          </div>
        </div>

        {/* Resume honesty audit — surfaces any fabrications introduced by the tailoring step */}
        <div className="mb-4" data-testid="dashboard-integrity-wrap" id="integrity">
          <IntegrityPanel analysisId={id} />
        </div>
        <ReportTabStrip
          testIdPrefix="dashboard-tab"
          tabs={[
            { id: "overview", label: "Overview", sectionTestId: "dashboard-section-overview" },
            { id: "tailored", label: "Tailored Resume", sectionTestId: "dashboard-section-tailored" },
            ...(analysis.cover_letter
              ? [{ id: "cover", label: "Cover Letter", sectionTestId: "dashboard-section-cover" }]
              : []),
            ...(analysis.company_research || analysis.interview_prep
              ? [{ id: "research", label: "Company & Interview", sectionTestId: "dashboard-section-research" }]
              : []),
          ]}
        />
        <div className="space-y-3" data-testid="dashboard-sections">
          <ResultSection
            title="Overview"
            eyebrow="Fit, problem, company snapshot"
            accentGradient="linear-gradient(135deg,#FBBF24 0%,#CA8A04 100%)"
            defaultOpen
            group="dashboard-result"
            testid="dashboard-section-overview"
          >
            <OverviewTab problem={problem} company={company} fit={fit} analysisId={analysis?.id} />
          </ResultSection>

          <ResultSection
            title="Tailored Resume"
            eyebrow="Your resume, re-framed for this job"
            accentGradient="linear-gradient(135deg,#F97316 0%,#B91C1C 100%)"
            group="dashboard-result"
            testid="dashboard-section-tailored"
          >
            <TailoredResumeTab
              tailored={tailored}
              tweaks={tweaks}
              copied={copied}
              onCopy={handleCopy}
              onDownloadPdf={handleDownloadPdf}
              onDownloadDocx={handleDownloadDocx}
              onOpenRetailor={() => { setShowRetailor(true); setTonePreset(""); setCustomTone(""); setRetailorError(""); }}
              onOpenInStudio={handleOpenInStudio}
              openingStudio={openingStudio}
            />
          </ResultSection>

          {analysis.cover_letter && (
            <ResultSection
              title="Cover Letter"
              eyebrow="Personalised draft"
              accentGradient="linear-gradient(135deg,#0F1B2D 0%,#1E3A5F 100%)"
              group="dashboard-result"
              testid="dashboard-section-cover"
            >
              <CoverLetterTab
                coverLetter={analysis.cover_letter}
                coverLoading={coverLoading}
                coverCopied={coverCopied}
                onRequestCover={handleRequestCover}
                onCopyCover={handleCopyCover}
                onDownloadCoverPdf={handleDownloadCoverPdf}
                onEmailDraftCover={handleEmailDraftCover}
              />
            </ResultSection>
          )}

          {(analysis.company_research || analysis.interview_prep) && (
            <ResultSection
              title="Company & Interview"
              eyebrow="Research + prep playbook"
              accentGradient="linear-gradient(135deg,#3B82F6 0%,#1E40AF 100%)"
              group="dashboard-result"
              testid="dashboard-section-research"
            >
              <ResearchInterviewTab
                research={analysis.company_research}
                prep={analysis.interview_prep}
                analysisId={analysis.id}
              />
            </ResultSection>
          )}
        </div>

        <div className="mt-6" data-testid="dashboard-publish-wrap">
          <StudioPublishPanel analysisId={analysis.id} collapsible />
        </div>

        {/* Live openings scored against the resume being reviewed */}
        <JobMatchesPanel className="mt-6" limit={4} />

        <div className="mt-10 flex justify-center">
          <Link to="/feed-job" className="rm-btn rm-btn-secondary" data-testid="another-job-btn">
            Tailor for another job
          </Link>
        </div>
      </div>

      <ShareModal
        open={showShare}
        shareUrl={shareUrl}
        shareCopied={shareCopied}
        onClose={() => setShowShare(false)}
        onCopy={handleCopyShare}
        onRevoke={handleRevokeShare}
      />

      <RetailorModal
        open={showRetailor}
        retailoring={retailoring}
        retailorError={retailorError}
        tonePreset={tonePreset}
        customTone={customTone}
        onPickPreset={(v) => { setTonePreset(v); setCustomTone(""); }}
        onCustomToneChange={(v) => { setCustomTone(v); setTonePreset(""); }}
        onClose={() => setShowRetailor(false)}
        onSubmit={handleRetailor}
      />

      <AddInterviewModal
        open={showAddInterview}
        onClose={() => setShowAddInterview(false)}
        onCreated={(iv) => { setShowAddInterview(false); navigate(`/interviews/${iv.id}`); }}
        analysisId={id}
        defaultRoleTitle={analysis?.job_title || ""}
        defaultRoleCompany={analysis?.company_name || ""}
      />
    </div>
  );
}
